import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Text } from '../../../../components';
import type { TenantSourceResult } from '../../creatives-tenant-source';
import type { GameSourceRecord } from '../../game-sources';
import {
  collectionGames,
  collectionsByPlace,
  collectionsBySite,
  siteLabel,
  MARKET_SITE,
  type MarketCollection,
  type MarketCollectionsAnswer,
} from '../../market-collections-api';
import { filterTerms, foldText } from '../../site-games';
import { useGameThumb } from '../../useGameThumb';
import * as S from './MarketCollectionPicker.styles';

/** How many of a collection's first games its card shows. */
const TILES = 4;

export type MarketCollectionPickerProps = {
  /** Reads the market's collections; called on mount and again on Retry. */
  load: (signal: AbortSignal) => Promise<TenantSourceResult<MarketCollectionsAnswer>>;
  /** Whether the brand's catalogue carries a game. */
  has: (id: string) => boolean;
  /** False while the brand's catalogue is still loading: a collection then shows its game count alone, and can be
   *  picked only once the catalogue is in, as only then is it known which of its games the list can hold. */
  catalogueReady: boolean;
  /** The brand's catalogue could not be read: no collection can say which of its games the list can hold, so the
   *  collections give way to that, with `onRetryCatalogue` to read it again. */
  catalogueFailed?: boolean;
  onRetryCatalogue?: () => void;
  /** A game's record in the brand's catalogue, for its tile's art (read by id when the record is a slim one). */
  gameOf?: (id: string) => GameSourceRecord | undefined;
  /** A game's name in the brand's catalogue, for its tile and the search; the collection's own name for it otherwise. */
  gameName?: (id: string) => string | undefined;
  /** The collection selected: its radio checked, and its casino the one shown when the step opens. */
  selectedSourceId?: string;
  /** A collection picked (selected), with the games of it the catalogue carries, in the collection's order. */
  onPick: (collection: MarketCollection, gameIds: string[]) => void;
  /** The heading, which the editor hands the keyboard when the step comes on show. */
  headingRef?: React.Ref<HTMLHeadingElement>;
};

/** One of a collection's first games: its art, read by id from a slim record (`useGameThumb`), else its name, which
 *  also stands while the art is on its way. */
const CollectionTile = ({ game, name }: { game: GameSourceRecord | undefined; name: string }): React.ReactElement => {
  const { src } = useGameThumb(game);
  return (
    <S.Tile>{src ? <img src={src} alt="" loading="lazy" draggable={false} /> : <S.TileName>{name}</S.TileName>}</S.Tile>
  );
};

type Read =
  | { state: 'loading' }
  | { state: 'failed'; message?: string }
  | { state: 'done'; answer: MarketCollectionsAnswer };

/** The market's own collections' mark, a star, outlined like the hub's icons: the shared set has none. */
const StarGlyph = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 2.8l2.8 5.9 6.4.8-4.7 4.4 1.2 6.4L12 17.2l-5.7 3.1 1.2-6.4-4.7-4.4 6.4-.8z" />
  </svg>
);

/** A casino's initials for its mark: `codere` -> `CO`, `big_bola` -> `BB`. */
const initials = (site: string): string => {
  const words = siteLabel(site).split(' ').filter(Boolean);
  return (words.length > 1 ? words[0][0] + words[1][0] : siteLabel(site).slice(0, 2)).toUpperCase();
};

/**
 * Step 2 of a list started from the market: the game collections published for the market of the brand's site, as a
 * list and its detail. The casinos run down the left (the market's own collections first, as "All casinos", then every
 * casino by name, each with how many collections it has); the chosen casino's collections are on the right, grouped by
 * where they sit on its site. A collection is a radio: picking one selects it (the editor's Continue moves on), handing
 * over its games, in its order, that the brand's catalogue carries. One none of whose games the catalogue carries cannot
 * be picked.
 *
 * The search finds collections by their name, their casino, where they sit and their games, across every casino at
 * once: each casino's count follows it, and when the chosen casino has no match left, the first one that has some is
 * chosen instead.
 */
export const MarketCollectionPicker = ({
  load,
  has,
  catalogueReady,
  catalogueFailed = false,
  onRetryCatalogue,
  gameOf,
  gameName,
  selectedSourceId,
  onPick,
  headingRef,
}: MarketCollectionPickerProps): React.ReactElement => {
  const { t } = useTranslation();
  const [read, setRead] = React.useState<Read>({ state: 'loading' });
  const [attempt, setAttempt] = React.useState(0);
  const [query, setQuery] = React.useState('');
  const [chosen, setChosen] = React.useState<string | null>(null);
  const radioName = React.useId();
  // The selection only decides where a read opens, so a new pick does not read the collections again.
  const selectedRef = React.useRef(selectedSourceId);
  selectedRef.current = selectedSourceId;

  // Synchronises with the service: the collections are read on mount, and again on Retry. The casino shown first is
  // the selected collection's, else the first one (the market's own).
  React.useEffect(() => {
    const controller = new AbortController();
    setRead({ state: 'loading' });
    void load(controller.signal).then((result) => {
      if (controller.signal.aborted) return;
      if (!result.ok) {
        setRead({ state: 'failed', message: result.reason === 'refused' ? result.message : undefined });
        return;
      }
      setRead({ state: 'done', answer: result.value });
      const selected = result.value.collections.find((c) => c.sourceId !== '' && c.sourceId === selectedRef.current);
      setChosen(selected ? selected.site : collectionsBySite(result.value.collections)[0]?.site ?? null);
    });
    return () => controller.abort();
  }, [load, attempt]);

  const nameOfGame = React.useCallback((id: string, own: string): string => gameName?.(id) || own, [gameName]);

  /** Every word of the query in what is said about a collection: its name, its casino, where it sits, its games. */
  const matches = React.useCallback(
    (collection: MarketCollection, text: string): boolean => {
      const words = filterTerms(text);
      if (words.length === 0) return true;
      const said = foldText(
        [
          collection.name,
          siteLabel(collection.site),
          collection.site === MARKET_SITE ? t('All casinos') : '',
          collection.page,
          collection.category,
          ...collection.games.map((game) => nameOfGame(game.id, game.name)),
        ].join(' '),
      );
      return words.every((word) => said.includes(word));
    },
    [nameOfGame, t],
  );

  // Every casino, in the order the owner reads them, with the collections of it that match the search.
  const sites = React.useMemo(
    () =>
      read.state === 'done'
        ? collectionsBySite(read.answer.collections).map(({ site, collections }) => ({
            site,
            matching: collections.filter((c) => matches(c, query)),
          }))
        : [],
    [read, query, matches],
  );

  /** A new search: when the chosen casino has no match left, the first one that has some is chosen. */
  const search = (next: string): void => {
    setQuery(next);
    if (read.state !== 'done') return;
    const bySite = collectionsBySite(read.answer.collections);
    const hits = (site: string): boolean =>
      bySite.some((group) => group.site === site && group.collections.some((c) => matches(c, next)));
    if (chosen !== null && hits(chosen)) return;
    const first = bySite.find((group) => hits(group.site));
    if (first) setChosen(first.site);
  };

  const nameOf = (site: string): string => (site === MARKET_SITE ? t('All casinos') : siteLabel(site));

  const answer = read.state === 'done' ? read.answer : null;
  const shown = sites.find((entry) => entry.site === chosen) ?? sites[0];

  const renderCard = (collection: MarketCollection): React.ReactElement => {
    const { inCatalogue } = collectionGames(collection, has);
    const none = catalogueReady && inCatalogue.length === 0;
    const off = none || !catalogueReady;
    const selected = collection.sourceId !== '' && collection.sourceId === selectedSourceId;
    const total = collection.games.length;
    // An element id, which aria-labelledby and aria-describedby split on whitespace: the source id made safe for one.
    const id = `${radioName}-${(collection.sourceId || collection.name).replace(/[^A-Za-z0-9_-]/g, '_')}`;
    const carried = !catalogueReady
      ? null
      : none
        ? t('None in your catalogue')
        : inCatalogue.length === total
          ? t('All in your catalogue')
          : t('{{m}} in your catalogue', { m: inCatalogue.length });
    return (
      <S.Card key={collection.sourceId || collection.name} $on={selected} $off={off}>
        <S.Radio
          type="radio"
          name={radioName}
          value={collection.sourceId}
          checked={selected}
          disabled={off}
          onChange={() => onPick(collection, inCatalogue)}
          aria-labelledby={`${id}-name`}
          aria-describedby={`${id}-meta`}
        />
        <S.Tiles aria-hidden="true">
          {collection.games.slice(0, TILES).map((game) => (
            <CollectionTile key={game.id} game={gameOf?.(game.id)} name={nameOfGame(game.id, game.name)} />
          ))}
        </S.Tiles>
        <S.NameRow>
          <S.Name id={`${id}-name`}>{collection.name}</S.Name>
          <S.Mark $on={selected} aria-hidden="true" />
        </S.NameRow>
        <S.Meta id={`${id}-meta`}>
          <S.GamesChip>{total === 1 ? t('1 game') : t('{{n}} games', { n: total })}</S.GamesChip>
          {carried ? (
            <S.Carried>
              {none ? null : <Icon name="check" size={14} />}
              {carried}
            </S.Carried>
          ) : null}
        </S.Meta>
      </S.Card>
    );
  };

  // What the collections' room holds. The heading over it is one element whatever the room holds (reading, a failed
  // read, nothing published, the collections), so the keyboard the step hands it stays there as the read settles.
  let body: React.ReactNode;
  if (read.state === 'loading') {
    body = (
      <S.State role="status" aria-busy="true">
        <Text tag="span" text="Reading your market's collections…" />
      </S.State>
    );
  } else if (read.state === 'failed') {
    body = (
      <S.State role="alert">
        {read.message ? (
          <Text tag="span" text={read.message} raw />
        ) : (
          <Text tag="span" text="Your market's collections could not be read" />
        )}
        <Button variant="outline" label={t('Retry')} onClick={() => setAttempt((n) => n + 1)} />
      </S.State>
    );
  } else if (read.answer.collections.length === 0) {
    body = (
      <S.State role="status">
        <Text tag="span" text="No collections are published for your market yet" />
      </S.State>
    );
  } else if (catalogueFailed) {
    // Without the catalogue no collection can say what the list would hold: said as that, not as "None in your
    // catalogue" on every card.
    body = (
      <S.State role="alert">
        <Text
          tag="span"
          text="The game catalogue could not be loaded, so this list's games cannot be shown. Please try again."
        />
        {onRetryCatalogue ? <Button variant="outline" label={t('Try again')} onClick={onRetryCatalogue} /> : null}
      </S.State>
    );
  } else if (!shown || shown.matching.length === 0) {
    body = (
      <S.State role="status">
        <Text tag="span" text="No collection matches your search" />
      </S.State>
    );
  } else {
    body = collectionsByPlace(shown.matching).map((group) => (
      <S.Group key={`${group.page}|${group.category}`}>
        <S.GroupHead>
          <S.GroupTitle>
            {group.page ? <span>{group.page}</span> : null}
            {group.page && group.category ? <span aria-hidden="true">›</span> : null}
            {group.category ? <S.GroupCategory>{group.category}</S.GroupCategory> : null}
          </S.GroupTitle>
          <S.GroupRule aria-hidden="true" />
          <S.GroupCount>
            {group.items.length === 1 ? t('1 collection') : t('{{n}} collections', { n: group.items.length })}
          </S.GroupCount>
        </S.GroupHead>
        <S.Cards>{group.items.map(renderCard)}</S.Cards>
      </S.Group>
    ));
  }

  const browsing = answer !== null && answer.collections.length > 0;
  const title = !browsing
    ? t('Pick a collection')
    : shown?.site === MARKET_SITE
      ? t('Collections across all casinos')
      : t('{{casino}} collections', { casino: shown ? nameOf(shown.site) : '' });

  return (
    <S.Root>
      <S.Side>
        <S.SideTitle text="Casinos in your market" />
        <S.Sites role="group" aria-label={t('Casinos')}>
          {sites.map(({ site, matching }) => {
            const on = site === shown?.site;
            return (
              <S.Site
                key={site}
                type="button"
                $on={on}
                $none={matching.length === 0}
                aria-pressed={on}
                onClick={() => setChosen(site)}
              >
                <S.SiteMark $on={on} aria-hidden="true">
                  {site === MARKET_SITE ? <StarGlyph /> : initials(site)}
                </S.SiteMark>
                <S.SiteName $on={on}>{nameOf(site)}</S.SiteName>
                <S.SiteCount $on={on}>{matching.length}</S.SiteCount>
              </S.Site>
            );
          })}
        </S.Sites>
      </S.Side>
      <S.Main>
        <S.Head>
          <S.HeadText>
            <S.Title ref={headingRef} tabIndex={-1}>
              {title}
            </S.Title>
            <S.Hint text="Pick one to start from. Its games, in order, become your list, and you can edit them after." />
          </S.HeadText>
          {browsing ? (
            <S.SearchWrap>
              <Icon name="search" size={18} />
              <S.Search
                type="search"
                value={query}
                onChange={(event) => search(event.target.value)}
                placeholder={t('Search collections or games')}
                aria-label={t('Search collections or games')}
              />
            </S.SearchWrap>
          ) : null}
        </S.Head>
        <S.Scroll role="radiogroup" aria-label={t('Collections')} aria-busy={read.state === 'loading' || undefined}>
          {body}
        </S.Scroll>
      </S.Main>
    </S.Root>
  );
};

export default MarketCollectionPicker;
